/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useEffect } from "react";
import { applyJoinAppearance } from "./joinAppearance";
import { updateJoinAccount } from "./joinAccountStorage";
import type { Session } from "../ClientContext";

export const JoinBridgeFrame: FC = () => {
  useEffect(() => {
    const admin = import.meta.env.VITE_ADMIN_URL;
    const guest = import.meta.env.VITE_GUEST_URL;
    if (
      !admin ||
      !guest ||
      window.parent === window ||
      window.location.origin !== new URL(guest).origin
    )
      return;
    const origin = new URL(admin).origin;
    let busy = false,
      closed = false;
    const message = async (event: MessageEvent): Promise<void> => {
      if (
        busy ||
        event.origin !== origin ||
        event.source !== window.parent ||
        !["element-join-hello", "element-join-adopt", "element-join-account"].includes(
          event.data?.type,
        ) ||
        typeof event.data.nonce !== "string" ||
        event.data.nonce.length > 128
      )
        return;
      busy = true;
      try {
        if (event.data.type === "element-join-account") {
          let success=false;
          try {
            if(typeof event.data.userId!=="string" || event.data.userId.length>512)throw Error("Invalid account");
            await updateJoinAccount(event.data.command,event.data.userId,event.data.code);
            success=true;
          } catch { /* Report failure without exposing credentials. */ }
          if(!closed)window.parent.postMessage({type:"element-join-account-result",nonce:event.data.nonce,success},origin);
          return;
        }
        if (event.data.type === "element-join-adopt") {
          let success = false;
          try {
            if (!/^[A-Za-z0-9_-]{43}$/.test(event.data.ticket)) return;
            const raw = localStorage.getItem("matrix-auth-store");
            const session = raw ? (JSON.parse(raw) as Session) : null;
            if (session?.access_token) {
              const adopted = await fetch("/api/auth/join-session/adopt", {
                method: "POST",
                credentials: "same-origin",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({ ticket: event.data.ticket }),
              });
              success = adopted.ok;
            }
          } catch {
            /* The existing join session can still enter the call. */
          }
          if (!closed)
            window.parent.postMessage(
              {
                type: "element-join-adopt-result",
                nonce: event.data.nonce,
                success,
              },
              origin,
            );
          return;
        }
        applyJoinAppearance(event.data.appearance);
        const response = await fetch("/api/auth/join-session/challenge", {
          method: "POST",
          credentials: "same-origin",
        });
        if (!response.ok) throw new Error("Join browser check failed");
        const { challenge } = await response.json();
        let userId: string | undefined;
        try {
          const raw = localStorage.getItem("matrix-auth-store"),
            session = raw ? (JSON.parse(raw) as Session) : null;
          if (session?.access_token) {
            const me = await fetch("/api/me", {
              headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (me.ok) {
              const data = await me.json();
              if (
                ["owner", "organizer"].includes(data.role) &&
                data.userId === session.user_id
              )
                userId = data.userId;
            }
          }
        } catch {
          /* The normal one-use handoff will handle a missing/expired session. */
        }
        if (!closed)
          window.parent.postMessage(
            {
              type: "element-join-challenge",
              nonce: event.data.nonce,
              challenge,
              userId,
            },
            origin,
          );
      } finally {
        busy = false;
      }
    };
    const listener = (event: MessageEvent): void => {
      void message(event).catch(() => {});
    };
    window.addEventListener("message", listener);
    window.parent.postMessage({ type: "element-join-ready" }, origin);
    return () => {
      closed = true;
      window.removeEventListener("message", listener);
    };
  }, []);
  return null;
};
